import GeneralCommentIcon from 'components/atoms/icons/generalCommentIcon';
import ControlPanelSuccessIcon from 'components/atoms/icons/controlPanelSuccessIcon';
import { useMemo, useState } from 'react';
import { makeid } from 'utils/helpers/makeid';
import CallControlIcon from 'components/atoms/icons/callControlIcon';
import SmsControlIcon from 'components/atoms/icons/smsControlIcon';
import { getPatientInfoKey } from 'infrastructure/hooks/patient/use-patient-info';
import { useQueryClient } from '@tanstack/react-query';
import useUser from 'utils/useUser';
import { useDoctorInfo } from 'infrastructure/hooks/doctor/use-doctor-info';
import { useAppSelector } from 'store';
import { isMobile } from 'utils/deviceHelper';
import {
  hasAdminPanelAccess,
  hasClinicalRole,
  hasPatientAcceptanceRole,
} from 'utils/accessManager/helpers';
import { isAdmin } from 'utils/userTypeHelper';
import {
  useCallButtons,
  useSmsButtons,
  useSendSms,
} from 'infrastructure/hooks/control-panel';
import { ModuleType, PlatformEnums } from 'infrastructure/enums';
import { useSendComment } from 'infrastructure/hooks/comments/use-send';
import { useTranslation } from 'react-i18next';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { useSendScheduledMessage } from 'infrastructure/hooks/smsDashboard/useSendScheduledMessage';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useAvailabilityAlert } from 'components/molecules/smsDashboard/availabilityAlert/use-availability-alert';

import type { ISendSmsDataProps } from 'infrastructure/hooks/control-panel/api';
import type { ICreateComment, IPatient } from 'infrastructure/interfaces';

interface Props {
  patientGuid: string;
}

export const useInteractionActionButtons = ({ patientGuid }: Props) => {
  const { t } = useTranslation();
  const [clinicalNotesModule, setClinicalNotesModule] = useState(
    ModuleType.RPM,
  );
  const [clinicalNotesOpen, setClinicalNotesPopupOpen] = useState(false);
  const [commentsPopupOpen, setCommentsPopupOpen] = useState(false);
  const [smsPopupOpen, setSmsPopupOpen] = useState(false);
  const [isPrimaryNumber, setIsPrimaryNumber] = useState(false);
  const [isRpmSms, setIsRpmSms] = useState(true);
  const device = useAppSelector((state) => state.user.deviceType);
  const { roleNames, userType, timezone } = useUser();
  const queryClient = useQueryClient();
  const { checkAvailability, isLoading, Alert } = useAvailabilityAlert({
    patientGuid,
    withAlert: true,
  });
  const { sendSms, smsLoading: sendLoading } = useSendSms();
  const { sendScheduledMessage, sendScheduledMessageLoading } =
    useSendScheduledMessage(
      {},
      {
        onSuccess() {
          notificationController.info({
            message: t(
              'smsDashboard.message_scheduled_for_closest_available_time',
            ),
          });
        },
      },
    );

  const smsLoading = isLoading || sendLoading || sendScheduledMessageLoading;

  const { loading: commentLoading, sendComment } = useSendComment();

  const patientInfoKey = getPatientInfoKey(patientGuid);
  const cachedPatientInfo = queryClient.getQueryData<IPatient>(patientInfoKey);
  const { doctorInfo, doctorInfoLoading } = useDoctorInfo({
    doctorGuid: cachedPatientInfo?.doctorGuid,
  });

  const handleSubmit = async (data: ISendSmsDataProps) => {
    const patientPhone = data.isPrimaryNumber
      ? cachedPatientInfo?.loginInfo?.phone
      : cachedPatientInfo?.homeNumber;
    const { sendNow, sendLater, nextAvailableTime } = await checkAvailability(
      patientPhone!,
    );

    if (sendNow) {
      const res = await sendSms(data);
      return res;
    }

    if (sendLater && patientPhone && nextAvailableTime) {
      const res = await sendScheduledMessage({
        message: data.message,
        from: data.isRpmPanel ? PlatformEnums.RPM : PlatformEnums.CCM,
        patientPhone,
        scheduledTime: nextAvailableTime,
        patientGuid,
      });
      return res;
    }

    return { error: null };
  };

  const { callButtons: rpmCallButtons, callButtonsAlert: rpmCallButtonsAlert } =
    useCallButtons({
      module: ModuleType.RPM,
      patientGuid,
      userType,
      doNotCall: cachedPatientInfo?.doNotCall,
      emergencyContactNumber: cachedPatientInfo?.emergencyContactNumber ?? '',
      patientHomeNumber: cachedPatientInfo?.homeNumber ?? '',
      patientPhone: cachedPatientInfo?.loginInfo?.phone ?? '',
      doctorPhone: doctorInfo?.phone,
      roleNames,
      patientTimeZone: timezone,
    });
  const { callButtons: ccmCallButtons, callButtonsAlert: ccmCallButtonsAlert } =
    useCallButtons({
      module: ModuleType.CCM,
      patientGuid,
      userType,
      doNotCall: cachedPatientInfo?.doNotCall,
      emergencyContactNumber: cachedPatientInfo?.emergencyContactNumber ?? '',
      patientHomeNumber: cachedPatientInfo?.homeNumber ?? '',
      patientPhone: cachedPatientInfo?.loginInfo?.phone ?? '',
      doctorPhone: doctorInfo?.phone,
      roleNames,
      patientTimeZone: timezone,
    });
  const prmSmsButtons = useSmsButtons({
    isAdminPanel: true,
    patientHasSecondaryNumber: Boolean(cachedPatientInfo?.homeNumber),
    setIsPrimaryNumber,
    setSmsPopupOpen: () => {
      setIsRpmSms(true);
      setSmsPopupOpen(true);
    },
    userType,
  });
  const ccmSmsButtons = useSmsButtons({
    isAdminPanel: true,
    patientHasSecondaryNumber: Boolean(cachedPatientInfo?.homeNumber),
    setIsPrimaryNumber,
    setSmsPopupOpen: () => {
      setIsRpmSms(false);
      setSmsPopupOpen(true);
    },
    userType,
  });
  const showCommentButton = useMemo(
    () =>
      hasPatientAcceptanceRole(roleNames) ||
      hasAdminPanelAccess(roleNames) ||
      hasClinicalRole(roleNames) ||
      isAdmin(userType),
    [],
  );

  const {
    isActiveRpmStatus,
    isBlockCcmModule,
    isCcmActionAllowed,
    isCcmActionDisabled,
  } = useAccess({
    patientGuid,
    module: ModuleType.ADMIN,
  });

  const rpmDisable = !isActiveRpmStatus;

  const ccmDisable =
    isBlockCcmModule || !isCcmActionAllowed || isCcmActionDisabled;

  const interactionButtons = [
    {
      key: makeid(5),
      label: 'General Comments',
      Icon: GeneralCommentIcon,
      loading: commentLoading,
      handleClick: () => {
        setCommentsPopupOpen(true);
      },
      render: showCommentButton,
    },
    {
      key: makeid(5),
      label: 'RPM Call',
      Icon: CallControlIcon,
      loading: false,
      options: rpmCallButtons,
      render: !isMobile(device),
    },
    {
      key: makeid(5),
      label: 'CCM Call',
      Icon: CallControlIcon,
      loading: smsLoading,
      options: ccmCallButtons,
      render: !isMobile(device),
    },
    {
      key: makeid(5),
      label: 'RPM SMS',
      Icon: SmsControlIcon,
      loading: smsLoading,
      options: prmSmsButtons,
      render: !isMobile(device),
    },
    {
      key: makeid(5),
      label: 'CCM SMS',
      Icon: SmsControlIcon,
      loading: false,
      options: ccmSmsButtons,
      render: !isMobile(device),
    },
    {
      key: makeid(5),
      label: t('labels.rpmClinicalNote'),
      Icon: ControlPanelSuccessIcon,
      handleClick: () => {
        setClinicalNotesPopupOpen(true);
        setClinicalNotesModule(ModuleType.RPM);
      },
      render: true,
      disabled: !rpmDisable,
    },
    {
      key: makeid(5),
      label: t('labels.ccmClinicalNote'),
      Icon: ControlPanelSuccessIcon,
      handleClick: () => {
        setClinicalNotesPopupOpen(true);
        setClinicalNotesModule(ModuleType.CCM);
      },
      render: true,
      disabled: !ccmDisable,
    },
  ].filter(({ render }) => render);

  const sendCommentHandler = async (data: ICreateComment) => {
    const response = await sendComment(data);
    setCommentsPopupOpen(false);
    return response;
  };

  return {
    interactionButtons,
    commentsPopupOpen,
    doctorInfoLoading,
    smsPopupOpen,
    isPrimaryNumber,
    isRpmSms,
    smsLoading,
    commentLoading,
    setCommentsPopupOpen,
    sendComment: sendCommentHandler,
    setSmsPopupOpen,
    sendSms: handleSubmit,
    rpmCallButtonsAlert,
    ccmCallButtonsAlert,
    clinicalNotesModule,
    clinicalNotesOpen,
    setClinicalNotesPopupOpen,
    Alert,
  };
};
